
import { Component, Prop, Vue } from 'vue-property-decorator';
import { customersApi, reportContentCategoriesApi } from '@/apis';
import OsDatatable from '@/components/core/OsDatatable.vue';
import { IExtendedDataTableHeader, IKeyValuePair } from '@/models/core';
import { IReportContentCategory, ReportAudience } from '@/models/application';
import OsCard from '@/components/core/card/OsCard.vue';
import OsCardText from '@/components/core/card/OsCardText.vue';
import OsSheet from '@/components/core/sheet/OsSheet.vue';
import OsSheetContent from '@/components/core/sheet/OsSheetContent.vue';
import OsTestReportForm from '@/components/shared/reports/OsTestReportForm.vue';
import OsToolbar from '@/components/core/OsToolbar.vue';
import OsRecipientForm from '@/components/shared/recipients/OsRecipientForm.vue';
import OsCloseButton from '@/components/core/OsCloseButton.vue';
import OsInputLookup from '@/components/core/input/OsInputLookup.vue';
import OsInputSelect from '@/components/core/input/OsInputSelect.vue';
import OsButton from '@/components/core/OsButton.vue';

@Component({
  components: {
    OsButton,
    OsInputSelect,
    OsInputLookup,
    OsCloseButton,
    OsRecipientForm,
    OsToolbar,
    OsTestReportForm,
    OsSheetContent,
    OsSheet,
    OsCardText,
    OsCard,
    OsDatatable
  }
})
export default class OsReportContentCategoriesOverview extends Vue {
  @Prop({ default: 'Assigned segments' })
  textHeader!: string;

  @Prop({ default: null, required: true })
  customerId!: string;

  @Prop({ default: null, required: true })
  audience!: ReportAudience;

  assignedReportCategories: IReportContentCategory[] = [];
  allReportCategories: IReportContentCategory[] = [];
  showAssignDialog = false;
  assignLoading = false;
  selectedCategoryIdToAssign: string | null = null;

  get unassignedCategories(): IReportContentCategory[] {
    return this.allReportCategories.filter(
      (arc) => !this.assignedReportCategories.find((asc) => asc.id === arc.id)
    );
  }

  get unassignedCategoriesAsKvp(): IKeyValuePair<string, string>[] {
    return this.unassignedCategories.map((c) => {
      return { key: c.name, value: c.id };
    });
  }

  get header(): IExtendedDataTableHeader[] {
    return [
      {
        text: 'Name',
        value: 'name'
      },
      {
        text: 'Status',
        width: '100px',
        value: 'state'
      },
      {
        text: '',
        value: 'id',
        icon: 'mdi-delete-outline',
        width: '15px',
        sortable: false,
        action: async (r: IReportContentCategory) => {
          if (
            await this.$global.dialogs.confirm(
              'Remove Report Segment',
              `Are you sure you want to unassign ${r.name} from this customer? This action will also remove the ${r.name} segment from all of the customer’s campaigns.`
            )
          ) {
            const result = await customersApi.removeReportContentCategory(
              this.customerId,
              this.audience,
              r.id
            );
            if (result) {
              await this.loadAssignedCategories();
              this.$global.dialogs.toast('Successfully removed', {
                color: 'success',
                icon: 'mdi-delete-outline'
              });
            }
          }
        },
        align: 'center'
      }
    ];
  }

  async assignCategory() {
    if (!this.selectedCategoryIdToAssign) {
      return;
    }

    this.assignLoading = true;

    try {
      await customersApi.assignReportContentCategory(
        this.customerId,
        this.audience,
        this.selectedCategoryIdToAssign
      );
      this.selectedCategoryIdToAssign = null;
      await this.loadAssignedCategories();
      this.showAssignDialog = false;
    } finally {
      this.assignLoading = false;
    }
  }

  async mounted() {
    await this.loadAssignedCategories();
  }

  async loadAssignedCategories() {
    this.allReportCategories =
      await reportContentCategoriesApi.getReportContentCategories();
    this.assignedReportCategories =
      await customersApi.getAssignedReportContentCategories(
        this.customerId,
        this.audience,
        true
      );
  }
}
